import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  LineChart, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, Legend, ReferenceLine
} from "recharts";

import {
  getPlatformSettings,
  fetchPlatformSettings,
  getFairSeedConfig,
} from "../lib/platformSettings";

import { FAIR_INVESTMENT_CONFIG } from "../config/fairSettings";

import ExecutiveCard from "../components/executive/ExecutiveCard";
import ExecutiveSection from "../components/executive/ExecutiveSection";
import ExecutiveChartCard from "../components/executive/ExecutiveChartCard";

import ExecutiveRecommendationPanel from "../components/executive/ExecutiveRecommendationPanel";
import { formatMoney } from "../utils/formatters";
import LoadingOrbit from "../components/LoadingOrbit";
import EconomicBasisDisclosure from "../components/EconomicBasisDisclosure";

export default function ScenarioInvestmentOptimization() {
  const [activeScenarios, setActiveScenarios] = useState([]);
  const [selectedScenarioId, setSelectedScenarioId] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [optimizationData, setOptimizationData] = useState([]);
  const [economicOptimal, setEconomicOptimal] = useState(null);
  const [optimizationBasis, setOptimizationBasis] = useState(null);
  const [bestPositiveInvestment, setBestPositiveInvestment] = useState(null);
  const [investment, setInvestment] = useState(0);
  const [selectedInvestment, setSelectedInvestment] = useState(0);
  const [analysisLoading, setAnalysisLoading] = useState(false);

  const analysisRequestId = useRef(0);
  const optimizationRequestId = useRef(0);

  useEffect(() => () => {
    analysisRequestId.current += 1;
    optimizationRequestId.current += 1;
  }, []);
  
  const [settings, setSettings] = useState(getPlatformSettings());
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  const [presentationMode, setPresentationMode] = useState(false);

  const formatAxisMoney = (value) => {
    const number = Number(value || 0);
    const abs = Math.abs(number);
    const sign = number < 0 ? "-" : "";

    if (abs >= 1_000_000) return `${sign}€${(abs / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `${sign}€${(abs / 1_000).toFixed(0)}K`;

    return `${sign}€${abs.toFixed(0)}`;
  };
  
  useEffect(() => {
    let isActive = true;

    const loadSettings = async () => {
      try {
        const freshSettings = await fetchPlatformSettings();
        if (!isActive) return;
        setSettings(freshSettings);
      } catch (err) {
        console.error("Error loading platform settings:", err);
      } finally {
        if (isActive) {
          setSettingsLoaded(true);
        }
      }
    };

    loadSettings();

    return () => {
      isActive = false;
    };
  }, []);

  const fairParams = useMemo(() => ({
    simulations: Number(settings.simulations || 5000),
    seed: getFairSeedConfig(settings).seed,
  }), [
    settings.simulations,
    settings.seedMode,
    settings.seedValue,
  ]);

  useEffect(() => {
    axios
      .get("/api/digital-risk/fair-scenarios/", {
        params: { status: "Active" },
      })
      .then((res) => setActiveScenarios(res.data))
      .catch(console.error);
  }, []);


  const runAnalysis = (investmentSnapshot, scenarioSnapshot = selectedScenarioId) => {
    if (!settingsLoaded || !scenarioSnapshot) return;

    const requestId = ++analysisRequestId.current;

    setAnalysisLoading(true);

    axios
      .get("/api/digital-risk/dashboard/risk-quantification-analysis/", {
        params: {
          scenario_id: scenarioSnapshot,
          simulate_control: investmentSnapshot > 0,
          investment: investmentSnapshot,
          ...fairParams,
        },
      })
      .then((res) => {
        if (requestId === analysisRequestId.current) {
          console.log("Analysis response:", res.data);
          setAnalysis(res.data);
          setInvestment(investmentSnapshot);
          setSelectedInvestment(investmentSnapshot);
        }
      })
      .catch((error) => {
        if (requestId === analysisRequestId.current) console.error(error);
      })
      .finally(() => {
        if (requestId === analysisRequestId.current) {
          setAnalysisLoading(false);
        }
      });
  };

  useEffect(() => {
    if (!settingsLoaded) return;

    if (!selectedScenarioId) {
      analysisRequestId.current += 1;
      setAnalysis(null);
      setInvestment(0);
      setSelectedInvestment(0);
      return;
    }

    setInvestment(0);
    setSelectedInvestment(0);
    runAnalysis(0, selectedScenarioId);
    // Scenario/settings changes establish the governed zero-investment baseline.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsLoaded, selectedScenarioId, fairParams.simulations, fairParams.seed]);

  useEffect(() => {
    if (!settingsLoaded) return;
    if (!selectedScenarioId) {
      optimizationRequestId.current += 1;
      setOptimizationData([]);
      setEconomicOptimal(null);
      setOptimizationBasis(null);
      setBestPositiveInvestment(null);
      return;
    }

    const requestId = ++optimizationRequestId.current;
    setOptimizationData([]);
    setEconomicOptimal(null);
    setBestPositiveInvestment(null);

    axios
      .get("/api/digital-risk/dashboard/scenario-investment-optimization/", {
        params: { scenario_id: selectedScenarioId, ...fairParams },
      })
      .then((res) => {
        if (requestId === optimizationRequestId.current) {
          setOptimizationData(res.data?.curve || []);
          setEconomicOptimal(res.data?.economic_optimal || res.data?.optimal || null);
          setOptimizationBasis({
            economicHorizonYears: res.data?.economic_horizon_years,
            economicBasis: res.data?.economic_basis,
          });
          setBestPositiveInvestment(res.data?.best_positive_investment || null);
        }
      })
      .catch((error) => {
        if (requestId === optimizationRequestId.current) console.error(error);
      });
  }, [
    settingsLoaded,
    selectedScenarioId,
    fairParams.simulations,
    fairParams.seed,
  ]);

  const investmentStep = Number(settings?.investmentStep || 50000);
  const investmentCeilingRatio = Number(settings?.investmentCeilingRatio || 0.75);

  const portfolioEalForCeiling = Number(
    analysis?.baseline?.expected_annual_loss ??
    analysis?.expected_annual_loss ??
    0
  );

  const dynamicInvestmentMax = Math.max(
    FAIR_INVESTMENT_CONFIG.max,
    Math.ceil(
      (portfolioEalForCeiling * investmentCeilingRatio) / investmentStep
    ) * investmentStep
  );

  const normalizeInvestment = (value) => {
    return Math.round(Number(value || 0) / investmentStep) * investmentStep;
  };

  const baselineCurvePct = (analysis?.curve || []).map((p) => ({
    ...p,
    probability_pct: Number(p.exceedance_probability || 0) * 100,
  }));

  const controlCurvePct = (analysis?.control_curve || []).map((p) => ({
    ...p,
    probability_pct: Number(p.exceedance_probability || 0) * 100,
  }));

  const roi = analysis?.impact?.roi || 0;
  const ealReduction = analysis?.impact?.eal_reduction || 0;
  const p95Reduction = analysis?.impact?.p95_reduction || 0;
  const netBenefit = analysis?.impact?.net_benefit || 0;

  // These values are presentation aliases only. The backend-selected optimum
  // point remains the sole authority; this page never searches the curve.
  const optimalInvestment = economicOptimal?.investment;
  const optimalEalReduction = economicOptimal?.eal_reduction
    ?? economicOptimal?.riskReduction
    ?? economicOptimal?.reduction;
  const optimalRoi = economicOptimal?.roi ?? economicOptimal?.roi_ratio;
  const optimalNetBenefit = economicOptimal?.net_benefit ?? economicOptimal?.netBenefit;
  const hasModeledOptimal = optimalInvestment !== null && optimalInvestment !== undefined;

  const selectedVsOptimal = !hasModeledOptimal
    ? null
    : investment < Number(optimalInvestment)
      ? "Selected investment is below the modeled economic optimum."
      : investment > Number(optimalInvestment)
        ? "Selected investment is above the modeled economic optimum."
        : "Selected investment is aligned with the modeled economic optimum.";

  const capitalPosition = !hasModeledOptimal
    ? "Modeled Economic Optimum Unavailable"
    : investment < Number(optimalInvestment)
      ? "Below Modeled Economic Optimum"
      : investment > Number(optimalInvestment)
        ? "Above Modeled Economic Optimum"
        : "Aligned with Modeled Economic Optimum";

  const exceedanceCurveData = baselineCurvePct.map((point, index) => ({
    loss: point.loss,
    baseline_probability: point.probability_pct,
    control_probability:
      controlCurvePct[index]?.probability_pct ?? null,
  }));

  return (
    <div className="space-y-6">

      <div className="flex justify-end">
        <button
          onClick={() => {
            const nextMode = !presentationMode;
            setPresentationMode(nextMode);

            if (nextMode && !document.fullscreenElement) {
              document.documentElement.requestFullscreen();
            }

            if (!nextMode && document.fullscreenElement) {
              document.exitFullscreen();
            }
          }}
          className={`
            px-4 py-2 rounded-xl text-sm border transition
            ${
              presentationMode
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"   
            }
          `}
        >
           🖥️ {presentationMode ? "Exit Presentation" : "Presentation Mode"}
        </button>
      </div>


      <section className="bg-white border rounded-2xl p-6 shadow-sm">
        <h2 className="text-2xl font-bold">Scenario Investment Optimization</h2>
        <p className="text-sm text-gray-500 mt-1">
          Optimize investment decisions for an individual FAIR scenario.
        </p>

        <div className="mt-6">
          <label className="text-sm font-medium text-gray-700">
            FAIR Scenario
          </label>

          <select
            value={selectedScenarioId}
            onChange={(e) => {
              // Invalidate the previous scenario immediately so its optimum can
              // never be presented beneath the newly selected scenario.
              optimizationRequestId.current += 1;
              setOptimizationData([]);
              setEconomicOptimal(null);
              setOptimizationBasis(null);
              setBestPositiveInvestment(null);
              setSelectedScenarioId(e.target.value);
            }}
            className="mt-2 border w-full rounded-xl px-4 py-3 bg-white"
          >
            <option value="">Select a scenario...</option>
            {activeScenarios.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name || s.scenario_name}
              </option>
            ))}
          </select>
        </div>
      </section>

      <div className="relative space-y-6">
       
          {analysisLoading && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center">
              <LoadingOrbit
                message=""
                submessage=""
              />
            </div>
          )}

       {analysis?.baseline && (
        <>
          <section className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <ExecutiveCard
                title="Expected Annual Loss"
                value={formatMoney(analysis.baseline.expected_annual_loss, settings.currency)}
            />
            <ExecutiveCard
                title="P90 Loss"
                value={formatMoney(analysis.baseline.p90, settings.currency)}
            />
            <ExecutiveCard
                title="P95 Loss"
                value={formatMoney(analysis.baseline.p95, settings.currency)}
            />
            <ExecutiveCard
                title="P99 Loss"
                value={formatMoney(analysis.baseline.p99, settings.currency)}
            />
          </section>

          <section className="bg-sky-900 mt-6 text-white rounded-2xl p-6 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6 items-center">
              <div className="md:col-span-1">
                <p className="text-sm uppercase tracking-wide text-gray-200">Investment to Analyze</p>
                <p className="text-4xl font-bold mt-2">
                  {formatMoney(selectedInvestment, settings.currency)}
                </p>
              </div>

              <div className="md:col-span-4">
                <input
                  type="range"
                  min={FAIR_INVESTMENT_CONFIG.min}
                  max={dynamicInvestmentMax}
                  step={investmentStep}
                  value={selectedInvestment}
                  onChange={(e) =>
                    setSelectedInvestment(normalizeInvestment(e.target.value))
                  }
                  className="
                    w-full h-2 rounded-lg appearance-none cursor-pointer
                    bg-gray-200
                    [&::-webkit-slider-thumb]:appearance-none
                    [&::-webkit-slider-thumb]:h-6
                    [&::-webkit-slider-thumb]:w-6
                    [&::-webkit-slider-thumb]:rounded-full
                    [&::-webkit-slider-thumb]:bg-cyan-400
                    [&::-webkit-slider-thumb]:shadow-lg
                    [&::-webkit-slider-thumb]:border-4
                    [&::-webkit-slider-thumb]:border-[#0B132B]
                  "
                />
                <div className="flex justify-between text-xs text-slate-200 mt-2">
                  <span>{formatMoney(FAIR_INVESTMENT_CONFIG.min, settings.currency)}</span>
                  <span>{formatMoney(dynamicInvestmentMax, settings.currency)}</span>
                </div>
                <div className="mt-4 flex items-center justify-between gap-4">
                  <p className="text-sm text-slate-100" role="status">
                    {selectedInvestment !== investment ? "Changes not yet applied" : "Current assumptions applied"}
                  </p>
                  <button type="button" onClick={() => runAnalysis(selectedInvestment)} disabled={!selectedScenarioId || selectedInvestment === investment} className="rounded-xl bg-cyan-400 px-4 py-2 text-sm font-semibold text-sky-950 disabled:cursor-not-allowed disabled:opacity-50">
                    {analysisLoading && selectedInvestment !== investment ? "Running..." : "Run Analysis"}
                  </button>
                </div>
              </div>
            </div>
          </section>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 mt-4 p-5 mb-2">
            <p className="text-sm leading-relaxed text-slate-700">
              Selected scenario investment of{" "}
              <strong>
                {formatMoney(investment, settings.currency)}
              </strong>{" "}
              reduces expected annual exposure by{" "}
              <strong>
                {formatMoney(ealReduction, settings.currency)}
              </strong>{" "}
              and lowers severe loss exposure for this FAIR scenario.
            </p>
          </div>

          <section className="grid grid-cols-1 md:grid-cols-4 mb-4 gap-4">
            <ExecutiveCard
                title="EAL Reduction"
                value={formatMoney(ealReduction, settings.currency)}
            />
             <ExecutiveCard
                title="P95 Reduction"
                value={formatMoney(p95Reduction, settings.currency)}
            />
            <ExecutiveCard
                title="ROI"
                value={`${Number(roi).toFixed(2)}x`}
            />
            <ExecutiveCard
                title="Net Benefit"
                value={formatMoney(netBenefit, settings.currency)}
            />
          </section>

          <ExecutiveSection title="MODELED ECONOMIC OPTIMUM">
            <div className="mb-5">
              <EconomicBasisDisclosure
                economicHorizonYears={optimizationBasis?.economicHorizonYears ?? settings.economicHorizonYears}
              />
            </div>
            {hasModeledOptimal ? (
              <div className="space-y-5" data-testid="modeled-optimal-investment">
                <div>
                  <p className="text-4xl font-bold text-slate-950">
                    {formatMoney(optimalInvestment, settings.currency)}
                  </p>
                  <p className="mt-2 text-sm text-slate-600">
                    Investment level that maximizes modeled Net Benefit across the complete modeled decision space.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                  <ExecutiveCard title="Economic Optimum" value={formatMoney(optimalInvestment, settings.currency)} />
                  <ExecutiveCard title="EAL Reduction at Optimum" value={optimalEalReduction == null ? "Unavailable" : formatMoney(optimalEalReduction, settings.currency)} />
                  <ExecutiveCard title="ROI at Optimum" value={optimalRoi == null ? "Unavailable" : `${Number(optimalRoi).toFixed(2)}x`} />
                  <ExecutiveCard title="Maximum Net Benefit" value={optimalNetBenefit == null ? "Unavailable" : formatMoney(optimalNetBenefit, settings.currency)} />
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                  <div className="flex flex-wrap gap-x-8 gap-y-2">
                    <span>Selected Investment: <strong>{formatMoney(investment, settings.currency)}</strong></span>
                    <span>Modeled Economic Optimum: <strong>{formatMoney(optimalInvestment, settings.currency)}</strong></span>
                  </div>
                  <p className="mt-2 font-medium">{selectedVsOptimal}</p>
                </div>
                {Number(optimalInvestment) === 0 && <p className="text-sm leading-relaxed text-slate-600">Under the accepted modeled economic objective, no positive investment generates a higher Net Benefit than retaining the current baseline.</p>}
                <p className="text-sm leading-relaxed text-slate-600">Technical risk reduction can continue beyond the economic optimum; the modeled economic optimum identifies where Net Benefit is highest, not where technical resilience is maximized.</p>
                {bestPositiveInvestment && (
                  <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="best-positive-investment">
                    <h3 className="text-sm font-semibold tracking-wide text-slate-800">BEST POSITIVE MODELED INVESTMENT</h3>
                    <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-4">
                      <ExecutiveCard title="Investment" value={formatMoney(bestPositiveInvestment.investment, settings.currency)} />
                      <ExecutiveCard title="EAL Reduction" value={formatMoney(bestPositiveInvestment.riskReduction ?? bestPositiveInvestment.reduction ?? 0, settings.currency)} />
                      <ExecutiveCard title="ROI" value={`${Number(bestPositiveInvestment.roi ?? bestPositiveInvestment.roi_ratio ?? 0).toFixed(2)}x`} />
                      <ExecutiveCard title="Net Benefit" value={formatMoney(bestPositiveInvestment.netBenefit ?? bestPositiveInvestment.net_benefit ?? 0, settings.currency)} />
                    </div>
                    <p className="mt-3 text-sm text-slate-600">This investment improves modeled resilience, but does not exceed the zero-investment economic baseline under the accepted Net Benefit objective.</p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-slate-600" role="status">Modeled economic optimum unavailable</p>
            )}
          </ExecutiveSection>

          <ExecutiveRecommendationPanel
            label={capitalPosition}
            description={
              <>
                {selectedVsOptimal || "Economic position is unavailable."}{" "}
                Additional investment may still improve technical resilience.
              </>
            }
            metricLabel="Selected Investment"
            metricValue={formatMoney(investment, settings.currency)}
            tone="neutral"
          />

          <ExecutiveChartCard title="Loss Exceedance Curve">
            <LineChart data={exceedanceCurveData}>
              <CartesianGrid strokeDasharray="3 3" />

              <XAxis
                dataKey="loss"
                type="number"
                domain={[0, "dataMax"]}
                tickFormatter={(v) => formatMoney(v, settings.currency)}
                tick={{ fontSize: 12 }}
              />

              <YAxis
                domain={[0, 100]}
                tickFormatter={(v) => `${Number(v).toFixed(0)}%`}
                tick={{ fontSize: 12 }}
              />

              <Tooltip
                formatter={(v, name) => [
                  `${Number(v).toFixed(2)}%`,
                  name === "baseline_probability"
                    ? "Baseline"
                    : "With Investment",
                ]}
                labelFormatter={(v) =>
                  `Loss: ${formatMoney(v, settings.currency)}`
                }
              />

              <Legend />

              <Line
                type="monotone"
                dataKey="baseline_probability"
                stroke="#64748B"
                strokeWidth={2}
                dot={false}
                name="Baseline"
              />

              {controlCurvePct.length > 0 && (
                <Line
                  type="monotone"
                  dataKey="control_probability"
                  stroke="#2563EB"
                  strokeWidth={3}
                  dot={false}
                  name="With Investment"
                />
              )}
            </LineChart>
          </ExecutiveChartCard>

          {optimizationData.length > 0 && (
            <ExecutiveChartCard title="Investment Optimization Curve">
              <LineChart data={optimizationData}>
                <CartesianGrid strokeDasharray="3 3" />

                <XAxis
                  dataKey="investment"
                  type="number"
                  tickFormatter={(v) => formatMoney(v, settings.currency)}
                  tick={{ fontSize: 12 }}
                />

                <YAxis
                  tickFormatter={(v) => formatMoney(v, settings.currency)}
                  tick={{ fontSize: 12 }}
                />

                <Tooltip
                  formatter={(v) => formatMoney(v, settings.currency)}
                  labelFormatter={(v) =>
                    `Investment: ${formatMoney(v, settings.currency)}`
                  
                  }
                />

                <Legend />

                <ReferenceLine
                  x={investment}
                  stroke="#1d942f"
                  strokeDasharray="4 4"
                  label="Selected Investment"
                />

                {hasModeledOptimal && (
                  <ReferenceLine
                    x={Number(optimalInvestment)}
                    stroke="#7c3aed"
                    strokeWidth={2}
                    strokeDasharray="6 3"
                    label={{ value: "Modeled Economic Optimum", position: "insideTopRight", fill: "#6d28d9" }}
                  />
                )}

                <YAxis
                  yAxisId="left"
                  tickFormatter={formatAxisMoney}
                  tick={{ fontSize: 12 }}
                  width={70}
                />

                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tickFormatter={formatAxisMoney}
                  tick={{ fontSize: 12 }}
                  width={70}
                />

                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="riskReduction"
                  name="Risk Reduction"
                  stroke="#2E7D32"
                  strokeWidth={3}
                  dot={false}

                />

                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="netBenefit"
                  name="Net Benefit"
                  stroke="#64748B"
                  strokeWidth={3}
                  dot={false}
                 
                />

              </LineChart>
              {hasModeledOptimal && (
                <p
                  className="mt-2 text-xs font-medium text-violet-700"
                  data-testid="modeled-optimum-chart-marker"
                  data-optimal-investment={String(optimalInvestment)}
                >
                  Modeled Economic Optimum — {formatMoney(optimalInvestment, settings.currency)}
                </p>
              )}
            </ExecutiveChartCard>
          )}

        </>
      )}
      </div>
    </div>
  );
}
